import { useState } from 'react';
import dynamic from 'next/dynamic';
import type { GetStaticProps, NextPage } from 'next';
import { useMedia } from '@stitch-fix/mode-react';
import { getApolloSsrClient } from '@stitch-fix/sf-next/server';
import { serverLogger } from '../../utils/loggers/server';
import { Layout } from '../../components/Layout';
import { FormContextProvider } from '../../components/Gifts/FormContext';
import { formatKey, getGiftCardsLogger } from '../../components/Gifts/utils';
import {
  GetGiftCardLayoutLandingPageDocument,
  GetGiftCardLayoutLandingPageQuery,
} from '../../components/Gifts/GiftCardsContent';
import { moduleComponentMap } from '../../components/Gifts/LandingPage';
import type { GiftCardLayout } from '../../types/definitions/vendor/graphql-schema.d';

const BuyWizard = dynamic(() => import('../../components/Gifts/BuyWizard'), {
  ssr: false,
});
const RedeemWizard = dynamic(
  () => import('../../components/Gifts/RedeemWizard'),
  {
    ssr: false,
  },
);

const logger = getGiftCardsLogger();

type GiftsPageProps = {
  landingPageContent: GetGiftCardLayoutLandingPageQuery;
};

export const getStaticProps: GetStaticProps<GiftsPageProps> = async () => {
  try {
    const apolloSsrClient = getApolloSsrClient({ req: null });
    const { data } =
      await apolloSsrClient.query<GetGiftCardLayoutLandingPageQuery>({
        query: GetGiftCardLayoutLandingPageDocument,
      });

    return {
      props: {
        landingPageContent: data,
      },
      revalidate: 300, // 5 minutes
    };
  } catch (error: unknown) {
    serverLogger({
      level: 'error',
      team: 'incentives',
      message: 'Required page data is undefined - /gifts',
      context: {
        error,
        page: '/gifts',
        severity: 'critical',
      },
    });

    throw new Error('Required data not available to render /gifts page');
  }
};

const GiftsPage: NextPage<GiftsPageProps> = (props: GiftsPageProps) => {
  const { landingPageContent } = props;

  if (typeof window !== 'undefined') {
    logger.log('landingPageContent', landingPageContent);
  }

  const { landingPage } = landingPageContent.giftCardLayout as GiftCardLayout;

  const [buyWizardOpen, setBuyWizardOpen] = useState(false);
  const [redeemWizardOpen, setRedeemWizardOpen] = useState(false);

  const isMobile = useMedia('(max-width: 900px)');

  return (
    <FormContextProvider>
      <Layout forceHideHeaderFooter={buyWizardOpen || redeemWizardOpen}>
        {!buyWizardOpen &&
          !redeemWizardOpen &&
          landingPage.moduleOrder.map((moduleName: string) => {
            // @ts-ignore Each module has a different shape
            const Component = moduleComponentMap[moduleName];

            return (
              <Component
                key={moduleName}
                isMobile={isMobile}
                openBuyWizard={() => {
                  logger.log('Calling openBuyWizard');
                  setBuyWizardOpen(true);
                }}
                openRedeemWizard={() => {
                  logger.log('Calling openRedeemWizard');
                  setRedeemWizardOpen(true);
                }}
                // @ts-ignore Each module has a different shape
                {...landingPage[formatKey('', moduleName)]}
              />
            );
          })}
        {buyWizardOpen && (
          <BuyWizard
            isMobile={isMobile}
            onClick={() => setBuyWizardOpen(false)}
          />
        )}
        {redeemWizardOpen && (
          <RedeemWizard
            isMobile={isMobile}
            onClick={() => setRedeemWizardOpen(false)}
          />
        )}
      </Layout>
    </FormContextProvider>
  );
};

export default GiftsPage;
